import React, { useState, useEffect } from 'react';
import { expensesApi, expenseCategoriesApi } from '../../services/api';
import { fmt, EXPENSE_CATEGORIES, validateImageFile } from '../../utils/billing';
import { useAuth } from '../../context/AuthContext';
import { useConfirm, useNotify } from '../../context/ConfirmContext';

export default function ExpensesPage({ hotel }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [form, setForm] = useState({
    category: 'Housekeeping', description: '', amount: '', date: new Date().toISOString().split('T')[0],
    paidBy: 'cash', notes: '', receiptData: null,
  });
  const [viewImg, setViewImg] = useState(null);
  const [editingExp, setEditingExp] = useState(null);
  const [customCategories, setCustomCategories] = useState([]);
  const [showCatForm, setShowCatForm] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load is redefined every render
  useEffect(() => { load(hotel); }, [hotel, dateFilter]);

  async function load(currentHotel) {
    const h = currentHotel || hotel;
    if (!h?.id) return;
    try {
      const [all, cats] = await Promise.all([
        expensesApi.list(h.id),
        expenseCategoriesApi.list()
      ]);
      if (!all) return;
      
      let filtered = all;
      if (dateFilter) filtered = all.filter(e => e.date === dateFilter);
      
      const mapped = filtered.map(e => ({
        ...e,
        paidBy: e.paid_by,
        addedBy: e.recorded_by,
        receiptData: e.bill_image_url
      }));
      
      setExpenses(mapped.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setCustomCategories(cats.map(c => ({ id: c.id, name: c.name })));
    } catch (err) { console.error('Load Error:', err); }
  }

  const FALLBACK_CATEGORIES = ['Housekeeping', 'Maintenance', 'Food & Beverage', 'Utilities', 'Salaries', 'Marketing', 'Laundry', 'Miscellaneous'];
  const allCategories = [...new Set([...FALLBACK_CATEGORIES, ...EXPENSE_CATEGORIES, ...customCategories.map(c => c.name)])];

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await validateImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(f => ({ ...f, receiptData: ev.target.result }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      await notify(err.message);
      e.target.value = ''; // Reset input
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!hotel?.id) { await notify('No hotel selected. Please select a hotel first.'); return; }
    if (!form.amount || Number(form.amount) <= 0) { await notify('Please enter a valid amount.'); return; }
    setLoading(true);
    try {
      await expensesApi.create({
        hotelId: hotel.id,
        category: form.category,
        description: form.description,
        amount: form.amount,
        date: form.date,
        paidBy: form.paidBy,
        notes: form.notes,
        billImageUrl: form.receiptData || null,
        recordedBy: user?.name
      });
      setShowForm(false);
      setForm({ category: 'Housekeeping', description: '', amount: '', date: new Date().toISOString().split('T')[0], paidBy: 'cash', notes: '', receiptData: null });
      await load(hotel);
    } catch (err) {
      console.error('Add expense error:', err);
      await notify('Failed to add expense: ' + err.message);
    }
    setLoading(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingExp) return;
    setLoading(true);
    try {
      await expensesApi.update(editingExp.id, {
        ...editingExp,
        billImageUrl: editingExp.receiptData
      });
      setEditingExp(null);
      await load(hotel);
    } catch (err) { await notify(err.message); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Expense', body: 'Delete this expense?', confirmText: 'Delete', danger: true })) return;
    try {
      await expensesApi.delete(id);
      load(hotel);
    } catch (err) { await notify(err.message); }
  };

  const total = expenses.reduce((a, e) => a + Number(e.amount), 0);
  const cashTotal = expenses
    .filter(e => String(e.paidBy || '').toLowerCase() === 'cash')
    .reduce((a, e) => a + Number(e.amount), 0);
  const nonCashTotal = total - cashTotal;
  const byCategory = {};
  expenses.forEach(e => { byCategory[e.category] = (byCategory[e.category] || 0) + Number(e.amount); });

  return (
    <div>
      {/* Summary */}
      <div className="stats-row" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="stat-card" style={{ '--lc': 'var(--danger)' }}>
          <div className="stat-lbl">Total Expenses</div>
          <div className="stat-val" style={{ color: 'var(--danger)', fontSize: 22 }}>{fmt(total)}</div>
          <div className="stat-sub">{dateFilter ? `On ${dateFilter}` : 'All time'}</div>
        </div>
        <div className="stat-card" style={{ '--lc': '#b91c1c' }}>
          <div className="stat-lbl">Cash From Drawer</div>
          <div className="stat-val" style={{ color: '#b91c1c', fontSize: 22 }}>{fmt(cashTotal)}</div>
          <div className="stat-sub">Reduces drawer cash</div>
        </div>
        <div className="stat-card" style={{ '--lc': 'var(--teal)' }}>
          <div className="stat-lbl">Non-Cash Expenses</div>
          <div className="stat-val" style={{ color: 'var(--teal)', fontSize: 22 }}>{fmt(nonCashTotal)}</div>
          <div className="stat-sub">UPI / bank / card</div>
        </div>
        <div className="stat-card">
          <div className="stat-lbl">Transactions</div>
          <div className="stat-val" style={{ fontSize: 30 }}>{expenses.length}</div>
          <div className="stat-sub">expense entries</div>
        </div>
      </div>

      <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
        Expenses marked as `Cash` reduce the shared drawer. `UPI`, `Bank Transfer`, `Card`, and `Petty Cash` stay outside the drawer balance.
      </div>

      {/* Category breakdown */}
      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-head"><span className="panel-title">Breakdown by Category</span></div>
        <div style={{ padding: '14px 18px' }}>
          {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
            <div key={cat} className="rev-bar-row">
              <div className="rev-bar-label">{cat}</div>
              <div className="rev-bar-bg">
                <div className="rev-bar-fill" style={{ width: `${(amt / total) * 100}%`, background: 'var(--danger)' }}></div>
              </div>
              <div className="rev-bar-amt" style={{ color: 'var(--danger)' }}>{fmt(amt)}</div>
            </div>
          ))}
          {Object.keys(byCategory).length === 0 && <div style={{ color: 'var(--muted)', fontSize: 12 }}>No data</div>}
        </div>
      </div>

      {/* Table */}
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Expense Log</span>
          <div className="panel-actions" style={{ display: 'flex', gap: '10px' }}>
            {user?.role === 'superadmin' && (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCatForm(true)} style={{ border: '1px solid var(--border)' }}>
                ⚙️ Categories
              </button>
            )}
            <button
              className={`btn btn-sm ${!dateFilter ? 'btn-teal' : 'btn-ghost'}`}
              onClick={() => setDateFilter('')}
              style={{ border: '1px solid var(--border)' }}
            >
              All Time
            </button>
            <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
              style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 7, padding: '7px 10px', color: 'var(--text)', fontFamily: 'var(--font-b)', fontSize: 12, outline: 'none' }}
            />
            <button className="btn btn-gold btn-sm" onClick={() => setShowForm(true)}>+ Add Expense</button>
          </div>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Paid By</th><th>Added By</th><th>Proof</th>{['superadmin', 'manager'].includes(user?.role) && <th style={{ textAlign: 'right' }}>Actions</th>}</tr></thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr><td colSpan={8}><div className="empty"><div className="empty-icon">💼</div>No expenses recorded</div></td></tr>
              ) : expenses.map(e => (
                <tr key={e.id}>
                  <td>{e.date}</td>
                  <td><span className="badge b-red">{e.category}</span></td>
                  <td>{e.description}</td>
                  <td style={{ fontWeight: 600, color: 'var(--danger)' }}>{fmt(e.amount)}</td>
                  <td style={{ textTransform: 'capitalize' }}>
                    {e.paidBy?.replace('_', ' ')}
                    {String(e.paidBy || '').toLowerCase() === 'cash' && (
                      <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--danger)', fontWeight: 700 }}>Drawer</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{e.addedBy || '—'}</td>
                  <td>
                    {e.receiptData ? (
                      <button className="btn btn-ghost btn-sm" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setViewImg(e.receiptData)}>
                        View Bill
                      </button>
                    ) : (
                      <span style={{ color: 'var(--muted2)', fontSize: 11 }}>—</span>
                    )}
                  </td>
                  {['superadmin', 'manager'].includes(user?.role) && (
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" title="Edit Expense" onClick={() => setEditingExp({ ...e })}>✏️</button>
                        <button className="btn btn-ghost btn-sm" title="Delete Expense" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(e.id)}>🗑️</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT EXPENSE MODAL */}
      {editingExp && (
        <div className="overlay" onClick={() => setEditingExp(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Edit Expense</div>
            <form onSubmit={handleUpdate}>
              <div className="form-row-2">
                <div className="field">
                  <label>Category *</label>
                  <select value={editingExp.category} onChange={e => setEditingExp(f => ({ ...f, category: e.target.value }))}>
                    {allCategories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Amount (₹) *</label>
                  <input type="number" value={editingExp.amount} onChange={e => setEditingExp(f => ({ ...f, amount: e.target.value }))} placeholder="0" required />
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Description *</label>
                <input value={editingExp.description} onChange={e => setEditingExp(f => ({ ...f, description: e.target.value }))} placeholder="What was purchased?" required />
              </div>
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Date</label>
                  <input type="date" value={editingExp.date} onChange={e => setEditingExp(f => ({ ...f, date: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Paid By</label>
                  <select value={editingExp.paidBy} onChange={e => setEditingExp(f => ({ ...f, paidBy: e.target.value }))}>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="petty_cash">Petty Cash</option>
                  </select>
                </div>
              </div>
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Notes</label>
                  <textarea value={editingExp.notes} onChange={e => setEditingExp(f => ({ ...f, notes: e.target.value }))} placeholder="Notes..." style={{ minHeight: 60 }} />
                </div>
                <div className="field">
                  <label>Update Proof (Image)</label>
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      await validateImageFile(file);
                      const reader = new FileReader();
                      reader.onload = (ev) => { setEditingExp(f => ({ ...f, receiptData: ev.target.result })); };
                      reader.readAsDataURL(file);
                    } catch (err) { await notify(err.message); }
                  }} style={{ marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', padding: 10, borderRadius: 6, width: '100%' }} />
                  {editingExp.receiptData && <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 8, fontWeight: 600 }}>✓ Proof attached</div>}
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={() => setEditingExp(null)}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={loading}>
                  {loading ? 'Updating…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD EXPENSE MODAL */}
      {showForm && (
        <div className="overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add Expense</div>
            <div className="modal-sub">{hotel?.name} · {form.date}</div>
            <form onSubmit={handleAdd}>
              <div className="form-row-2">
                <div className="field">
                  <label>Category *</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {allCategories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Amount (₹) *</label>
                  <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" required />
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Description *</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What was purchased/paid for?" required />
              </div>
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Date</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Paid By</label>
                  <select value={form.paidBy} onChange={e => setForm(f => ({ ...f, paidBy: e.target.value }))}>
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="petty_cash">Petty Cash</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--muted)' }}>
                Choose `Cash` only when money was physically taken from the drawer.
              </div>
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." style={{ minHeight: 60 }} />
                </div>
                <div className="field">
                  <label>Bill Proof (Image)</label>
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={handleFileChange} style={{ marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', padding: 10, borderRadius: 6, width: '100%' }} />
                  {form.receiptData && <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 8, fontWeight: 600 }}>✓ Proof attached</div>}
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={loading}>
                  {loading ? 'Adding…' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW IMAGE MODAL */}
      {viewImg && (
        <div className="overlay" onClick={() => setViewImg(null)} style={{ zIndex: 9999 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 650, padding: 15, background: 'var(--bg)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
              <div className="modal-title" style={{ marginBottom: 0 }}>Bill Proof</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewImg(null)}>✕ Close</button>
            </div>

            <div style={{ background: '#000', borderRadius: 8, padding: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              {viewImg.startsWith('data:application/pdf') ? (
                <iframe src={viewImg} title="Bill Proof PDF" width="100%" height="500px" style={{ border: 'none', background: '#fff' }} />
              ) : (
                <img src={viewImg} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* MANAGE CUSTOM CATEGORIES MODAL (SUPER ADMIN) */}
      {showCatForm && (
        <div className="overlay" onClick={() => setShowCatForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Manage Categories</div>
            <div className="modal-sub">Custom expenses cross-hotel</div>

            <div style={{ marginBottom: 15, maxHeight: 150, overflowY: 'auto' }}>
              {customCategories.length === 0 ? <p style={{ fontSize: 12, color: 'var(--muted)' }}>No custom categories added.</p> :
                customCategories.map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--bg)', marginBottom: 4, borderRadius: 4, border: '1px solid var(--border)' }}>
                    <span>{c.name}</span>
                    <button type="button" onClick={async () => {
                      await expenseCategoriesApi.delete(c.id);
                      load();
                    }} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>✕</button>
                  </div>
                ))
              }
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newCatName.trim()) return;
              await expenseCategoriesApi.create({ name: newCatName.trim() });
              setNewCatName('');
              load();
            }}>
              <div className="field">
                <input placeholder="New Category Name" value={newCatName} onChange={e => setNewCatName(e.target.value)} required />
              </div>
              <button type="submit" className="btn btn-teal btn-sm" style={{ marginTop: 10, width: '100%' }}>+ Add Custom Category</button>
            </form>
            <button className="btn btn-ghost" style={{ width: '100%', marginTop: 15 }} onClick={() => setShowCatForm(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
